import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  RocketLaunchIcon, SpinnerGapIcon, StarIcon, TrophyIcon, SealCheckIcon,
  ArrowSquareOutIcon, ClockIcon, InfoIcon, CaretDownIcon, CaretUpIcon, FireIcon,
} from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { fetchLaunchHub } from "../lib/api";
import { TradeButton } from "../components/TradeButton";
import { HitRateBadge, useDeployerStatsBatch } from "../components/HitRateBadge";
import { markCreatorSeen } from "../lib/creatorCoins";
import { fmtUsd } from "../lib/format";
import { shortAddr } from "../lib/solana";

const TF_META = [
  { key: "m5", label: "m5", common: "5-minute", use: "Scalping / precise entry timing" },
  { key: "h1", label: "h1", common: "1-hour", use: "Short-term structure & momentum" },
  { key: "h6", label: "h6", common: "6-hour", use: "Intermediate trend / key levels" },
  { key: "h24", label: "h24", common: "24-hour / daily", use: "Big-picture bias, major support/resistance — the overall \u201cfigure\u201d" },
];

const TAG_META = {
  creator: { label: "ALPHA CREATOR", color: "#F0C000", Icon: StarIcon, link: "/devs" },
  proven: { label: "PROVEN DEV", color: "#22D3EE", Icon: TrophyIcon, link: "/proven-devs" },
  builder: { label: "MY BUILDER", color: "#00E599", Icon: SealCheckIcon, link: "/my-builders" },
};

const fmtAlive = (h) => {
  if (h == null) return null;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 24) return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${Math.round(h % 24)}h`;
};

const pcColor = (v) => (v == null || v === 0 ? "#8A8F98" : v > 0 ? "#00E599" : "#FF3366");

const TfChip = ({ tf, value }) => (
  <div
    title={`${tf.common} (${tf.label}) — ${tf.use}`}
    data-testid={`hub-tf-${tf.key}`}
    className="flex flex-col items-center px-2 py-1 border bg-black/30 min-w-[52px]"
    style={{ borderColor: `${pcColor(value)}44` }}
  >
    <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{tf.label}</span>
    <span className="font-mono text-[11px] font-bold" style={{ color: pcColor(value) }}>
      {value == null ? "—" : `${value > 0 ? "+" : ""}${value}%`}
    </span>
  </div>
);

const Fallback = ({ symbol }) => (
  <div className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-xs text-muted-foreground shrink-0">
    {(symbol || "?").slice(0, 3)}
  </div>
);

const toneColor = { good: "#00E599", warn: "#F0A020", bad: "#FF3366", neutral: "#8A8F98" };

function LaunchCard({ item, ds }) {
  const coin = { address: item.mint, symbol: item.symbol, creator: item.creator, url: item.url };
  const mtf = item.mtf;
  const alive = fmtAlive(item.ageHours);
  return (
    <div
      data-testid={`hub-card-${item.mint}`}
      className="border bg-card p-4 space-y-3 transition-colors hover:border-white/25"
      style={{ borderColor: item.onCurve ? "rgba(240,192,0,0.45)" : "rgba(255,255,255,0.1)" }}
    >
      <div className="flex items-start gap-3">
        {item.image ? (
          <img src={item.image} alt={item.symbol} className="w-11 h-11 object-cover border border-white/10 shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
        ) : <Fallback symbol={item.symbol} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={`/coin/${item.mint}`} className="font-heading font-bold text-base truncate hover:text-neon transition-colors">
              ${(item.symbol || shortAddr(item.mint)).toUpperCase()}
            </Link>
            {item.onCurve && (
              <span data-testid={`hub-curve-${item.mint}`} className="font-mono text-[9px] px-1.5 py-0.5 font-bold animate-pulse" style={{ background: "#F0C00022", border: "1px solid #F0C000", color: "#F0C000" }}>
                🐣 ON CURVE {item.curveProgress != null ? `· ${item.curveProgress}%` : ""}
              </span>
            )}
            {!item.live && (
              <span className="font-mono text-[9px] px-1.5 py-0.5 border border-white/15 text-muted-foreground">NO LIVE PAIR</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">{item.name || shortAddr(item.mint)}</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {item.devTags?.map((t) => {
              const m = TAG_META[t];
              if (!m) return null;
              const Icon = m.Icon;
              return (
                <Link key={t} to={m.link} data-testid={`hub-tag-${t}-${item.mint}`}
                  title={t === "proven" && item.hitRate != null ? `On the Proven Devs board — ${item.hitRate}% hit rate` : `From your ${m.label.toLowerCase()} list`}
                  className="flex items-center gap-1 font-mono text-[9px] px-1.5 py-0.5 font-bold tracking-wide hover:opacity-80 transition-opacity"
                  style={{ background: `${m.color}1a`, border: `1px solid ${m.color}66`, color: m.color }}>
                  <Icon size={10} weight="fill" /> {m.label}{t === "proven" && item.hitRate != null ? ` ${item.hitRate}%` : ""}
                </Link>
              );
            })}
            <span className="font-mono text-[10px] text-muted-foreground truncate">{item.devName || shortAddr(item.creator)}</span>
            <HitRateBadge stats={ds} compact />
          </div>
        </div>
        {alive && (
          <span data-testid={`hub-age-${item.mint}`} title="How long this token has been alive" className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground shrink-0">
            <ClockIcon size={11} /> alive {alive}
          </span>
        )}
      </div>

      {item.live && !item.onCurve && mtf && (
        <>
          <div className="flex gap-1.5" data-testid={`hub-mtf-${item.mint}`}>
            {TF_META.map((tf) => <TfChip key={tf.key} tf={tf} value={mtf[tf.key]} />)}
          </div>
          <div title={mtf.note} className="font-mono text-[11px] flex items-center gap-1.5" style={{ color: toneColor[mtf.tone] || "#8A8F98" }}>
            {mtf.label}
          </div>
        </>
      )}
      {item.onCurve && (
        <div className="font-mono text-[11px] text-muted-foreground">
          Pre-DEX — still on the bonding curve{item.buys5 != null ? ` · ${item.buys5} buys/5m` : ""}. MTF unlocks once it migrates.
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {item.marketCap != null && <span className="font-mono text-[10px] px-2 py-1 border border-white/10 text-muted-foreground">MCap {fmtUsd(item.marketCap)}</span>}
        {item.liquidity != null && <span className="font-mono text-[10px] px-2 py-1 border border-white/10 text-muted-foreground">Liq {fmtUsd(item.liquidity)}</span>}
        {item.vol24h != null && <span className="font-mono text-[10px] px-2 py-1 border border-white/10 text-muted-foreground">24h Vol {fmtUsd(item.vol24h)}</span>}
        {item.score != null && <span className="font-mono text-[10px] px-2 py-1 border border-neon/25 text-neon">Score {item.score}</span>}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <TradeButton coin={coin} size="sm" />
        <Link to={`/coin/${item.mint}`} data-testid={`hub-detail-${item.mint}`} className="flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-xs font-semibold text-muted-foreground hover:text-white hover:border-white/30 transition-colors">
          <ArrowSquareOutIcon size={13} /> Details
        </Link>
      </div>
    </div>
  );
}

export default function LaunchHub() {
  const clientId = getClientId();
  const [filter, setFilter] = useState("all");
  const [showDead, setShowDead] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["launchHub", clientId],
    queryFn: () => fetchLaunchHub(clientId),
    refetchInterval: 45000,
  });

  const items = useMemo(() => data?.items || [], [data]);
  const stats = useDeployerStatsBatch(items.map((i) => i.creator));
  const counts = data?.counts || {};
  const filtered = items
    .filter((i) => (showDead ? true : i.live))
    .filter((i) => (filter === "all" ? true : i.devTags?.includes(filter)));

  useEffect(() => {
    if (items.length) markCreatorSeen(items.map((i) => i.mint));
  }, [items]);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="launch-hub-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><RocketLaunchIcon size={24} weight="fill" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">LAUNCH <span className="text-neon">HUB</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm max-w-3xl leading-relaxed" data-testid="launch-hub-explainer">
          Every launch from every dev you've saved — merged into <span className="text-white font-semibold">one newest-first feed</span> so
          you never have to hunt across pages. It pulls from your{" "}
          <Link to="/devs" className="hover:underline" style={{ color: "#F0C000" }}>⭐ Creators (Alpha list)</Link>, the{" "}
          <Link to="/proven-devs" className="hover:underline" style={{ color: "#22D3EE" }}>🏆 Proven Devs board</Link> and your{" "}
          <Link to="/my-builders" className="hover:underline" style={{ color: "#00E599" }}>🛠 My Builders</Link> follows.
          Each token shows how long it's been alive plus its price action across four timeframes (m5 · h1 · h6 · h24) and a
          multi-timeframe read — so you instantly know if it's pushing, coiling or fading.
        </p>
      </div>

      <div className="border border-white/10 bg-card" data-testid="hub-tf-legend">
        <button data-testid="hub-tf-legend-toggle" onClick={() => setLegendOpen((v) => !v)} className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors">
          <span className="flex items-center gap-2 text-sm font-semibold"><InfoIcon size={15} className="text-neon" /> How to read the timeframes</span>
          {legendOpen ? <CaretUpIcon size={14} /> : <CaretDownIcon size={14} />}
        </button>
        {legendOpen && (
          <div className="border-t border-white/10 px-4 py-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {TF_META.map((tf) => (
              <div key={tf.key} className="flex items-baseline gap-2 text-xs">
                <span className="font-mono font-bold text-neon w-8 shrink-0">{tf.label}</span>
                <span className="text-white/80 shrink-0">{tf.common}</span>
                <span className="text-muted-foreground">— {tf.use}</span>
              </div>
            ))}
            <div className="sm:col-span-2 text-[11px] font-mono text-muted-foreground mt-1">
              Read h6/h24 for the trend ("the figure"), then m5/h1 for your entry timing. Green = up, red = down over that window.
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex border border-white/10">
          {[
            ["all", "All", null],
            ["creator", "Creators", TAG_META.creator],
            ["proven", "Proven Devs", TAG_META.proven],
            ["builder", "My Builders", TAG_META.builder],
          ].map(([key, label, m]) => (
            <button key={key} data-testid={`hub-filter-${key}`} onClick={() => setFilter(key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold transition-colors ${filter === key ? "text-black" : "text-muted-foreground hover:text-white"}`}
              style={filter === key ? { background: m?.color || "#00E599" } : {}}>
              {label}
              <span className="font-mono text-[10px] opacity-70">
                {key === "all" ? items.length : counts[key] != null ? `${counts[key]} dev${counts[key] === 1 ? "" : "s"}` : ""}
              </span>
            </button>
          ))}
        </div>
        <button data-testid="hub-showdead-toggle" onClick={() => setShowDead((v) => !v)} className="font-mono text-xs transition-colors ml-auto" style={{ color: showDead ? "#F0A020" : "#666" }}>
          {showDead ? "hide tokens with no live pair" : `show tokens with no live pair (${items.filter((i) => !i.live).length})`}
        </button>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <SpinnerGapIcon size={32} className="animate-spin text-neon" />
          <span className="font-mono text-sm">Pulling every launch from your saved devs…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 bg-card py-20 flex flex-col items-center gap-4 text-center px-4" data-testid="hub-empty">
          <FireIcon size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground font-mono text-sm max-w-md">
            {data?.devCount === 0
              ? "No saved devs yet. Add devs on the Creators page, follow builders on My Builders, or let the Proven Devs board fill in — their launches will all land here."
              : "No launches match this filter right now. The feed refreshes automatically."}
          </p>
          {data?.devCount === 0 && (
            <div className="flex gap-2 flex-wrap justify-center">
              <Link to="/devs" data-testid="hub-go-devs" className="px-4 py-2 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors">Creators</Link>
              <Link to="/proven-devs" className="px-4 py-2 border border-white/15 font-semibold text-sm text-muted-foreground hover:text-white transition-colors">Proven Devs</Link>
              <Link to="/my-builders" className="px-4 py-2 border border-white/15 font-semibold text-sm text-muted-foreground hover:text-white transition-colors">My Builders</Link>
            </div>
          )}
        </div>
      ) : (
        <div data-testid="hub-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item) => <LaunchCard key={item.mint} item={item} ds={stats[item.creator]} />)}
        </div>
      )}
    </div>
  );
}
